import React, { useState, useRef, useEffect } from 'react';
import { Drawer, Button, Input, List, Avatar, Typography, Space, Tag, Divider } from 'antd';
import { RobotOutlined, SendOutlined, MessageOutlined, BulbOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import aiChatService from '../../services/aiChatService';

const { Text } = Typography;

const AIChatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'ai', content: 'Hi! I\'m your QQINV AI assistant. I can help you with inventory management, sales tracking, purchasing, and more. What would you like to know?' },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const messagesEndRef = useRef(null);
  const location = useLocation();

  // Debug log removed to prevent excessive console output

  // Get current page context for suggestions
  useEffect(() => {
    const currentPage = location.pathname.split('/')[1] || 'dashboard';
    setSuggestions(aiChatService.getPageContextSuggestions(currentPage));
  }, [location.pathname]);

  // Auto scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (messageText = null) => {
    const messageToSend = messageText || input.trim();
    if (!messageToSend) return;
    
    const userMsg = { role: 'user', content: messageToSend };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const response = await aiChatService.sendMessage([...messages, userMsg]);
      setMessages((prev) => [...prev, { role: 'ai', content: response }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages((prev) => [...prev, { 
        role: 'ai', 
        content: 'Sorry, I encountered an error. Please try again or check your API configuration.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    handleSend(suggestion);
  };

  return (
    <>
      {/* Floating trigger button */}
      <Button
        type="primary"
        shape="round"
        size="large"
        icon={<RobotOutlined />}
        onClick={() => setOpen(true)}
        style={{
          position: 'fixed',
          right: 24,
          bottom: 24,
          zIndex: 1000,
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
        }}
      >
        AI Chat
      </Button>

      {/* Drawer chat */}
      <Drawer
        title={<Space><RobotOutlined /> <span>QQINV AI Assistant</span></Space>}
        open={open}
        onClose={() => setOpen(false)}
        width={546}
        styles={{
          body: { padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }
        }}
      >
        {/* Messages area */}
        <div style={{ flex: 1, padding: '16px', overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
          <List
            dataSource={messages}
            split={false}
            renderItem={(m, idx) => (
              <List.Item style={{ padding: '8px 0', border: 'none' }}>
                <Space align="start" style={{ width: '100%' }}>
                  <Avatar 
                    icon={m.role === 'ai' ? <RobotOutlined /> : <MessageOutlined />}
                    style={{ 
                      backgroundColor: m.role === 'ai' ? '#1890ff' : '#52c41a',
                      flexShrink: 0
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <Text style={{ 
                      whiteSpace: 'pre-wrap', 
                      display: 'block',
                      padding: '8px 12px',
                      backgroundColor: m.role === 'ai' ? '#f6f8fa' : '#e6f7ff',
                      borderRadius: '8px',
                      marginBottom: '4px'
                    }}>
                      {m.content}
                    </Text>
                  </div>
                </Space>
              </List.Item>
            )}
          />
          <div ref={messagesEndRef} />
        </div>

        {/* Suggestions */}
        {suggestions.length > 0 && (
          <div style={{ padding: '8px 16px', borderTop: '1px solid #f0f0f0' }}>
            <Space size={[4, 4]} wrap>
              <BulbOutlined style={{ color: '#faad14' }} />
              <Text type="secondary" style={{ fontSize: '12px' }}>Quick suggestions:</Text>
            </Space>
            <div style={{ marginTop: '8px' }}>
              <Space size={[4, 4]} wrap>
                {suggestions.map((suggestion, idx) => (
                  <Tag
                    key={idx}
                    style={{ cursor: 'pointer', margin: '2px' }}
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    {suggestion}
                  </Tag>
                ))}
              </Space>
            </div>
          </div>
        )}

        {/* Input area */}
        <div style={{ padding: '16px', borderTop: '1px solid #f0f0f0' }}>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              placeholder="Ask about inventory, sales, purchasing..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onPressEnter={() => handleSend()}
              disabled={loading}
              style={{ borderRadius: '6px 0 0 6px' }}
            />
            <Button 
              type="primary" 
              icon={<SendOutlined />} 
              onClick={() => handleSend()} 
              loading={loading}
              style={{ borderRadius: '0 6px 6px 0' }}
            />
          </Space.Compact>
        </div>
      </Drawer>
    </>
  );
};

export default AIChatbot;
